import { useEffect, useState } from 'react'
import { fundsFor, groupLabel, groupsFor } from '../components/Promote'
import { api, type Promotable, type Scope, type SourceDocument } from '../api'
import { RowMenu } from '../components/RowMenu'
import { layerLabel, says, subjectText } from '../limits'

/**
 * Promoting a repeated limit to a group — TECHNICAL_DESIGN.md §7.1, §5.7.
 *
 * *"Everywhere else, a materialised copy is the defect the scope layer exists to
 * remove."* Until this there was no way to find one. §7.1 has everything migrate
 * portfolio-attached, so a firm-wide policy enters the store as forty copies by
 * design — and the moment somebody *notices* that is afterwards, reading the
 * book, which is exactly when the import's own candidate list has gone. This
 * looks at the store instead: across imports, across mailbox submissions, across
 * funds added months apart.
 *
 * **Proposing it is an author's act.** It writes `proposed` and binds nothing;
 * `approve` still decides and still shows the approver every fund the group
 * reaches (§5.6). The capability sat with `approver` until somebody asked why —
 * and the answer was that the original reasoning was about the act that takes
 * effect, not this one. Worse, nobody approves their own submission, so a
 * promotion proposed by an approver needed a *second* approver and a firm with
 * one could not promote at all.
 *
 * **A candidate, and the word is load-bearing.** Twelve funds at 2% may be one
 * house policy or twelve independent negotiations, and nothing in the data
 * distinguishes them. So this promotes nothing on its own: it needs the group
 * and the document that authorises the limit for that group, which is a person's
 * act against a piece of paper. `rules.propose` refuses a scope rule citing
 * nothing for the same reason.
 *
 * **What it creates is a house policy, whatever the copies were.** A scope rule
 * is firm policy by construction: `mandate_is_portfolio_scoped` refuses a
 * contractual limit attached to a group, because *a contract binds a
 * counterparty, not a region*. So promoting eight mandate limits does not move
 * eight contracts onto a group — it states that the firm imposes this on
 * everything in the group, which is a different claim and the only one a group
 * can carry. The screen says so before the click, because somebody promoting
 * what they think is a contractual term should see that it is about to become
 * something else.
 *
 * **It proposes a thirteenth rule and closes none of the twelve**, and the
 * screen says so rather than implying otherwise. That is a real limit of the
 * store today: an active rule can only be superseded by a proposal naming it in
 * `supersedes_id`, which is one rule, so one scope rule cannot close twelve fund
 * rules. Promoting and then leaving the copies means Lookup will show both — the
 * inherited limit and the fund's own, marked as competing — which is untidy and
 * honest, where a screen quietly hiding the copies would be neither.
 */
export function Promotions({ canAttach, firmWide = true }: { canAttach: boolean; firmWide?: boolean }) {
  const [candidates, setCandidates] = useState<Promotable[]>([])
  const [scopes, setScopes] = useState<Scope[]>([])
  const [docs, setDocs] = useState<SourceDocument[]>([])
  const [open, setOpen] = useState<string | null>(null)
  /** For somebody across the firm: the candidates with copies on one desk, or all. */
  const [deskShown, setDeskShown] = useState('')
  const [form, setForm] = useState({ scopeId: '', documentId: '', clause: '' })
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setError(null)
    try {
      const [found, groups, registry] = await Promise.all([
        api.promotionCandidates(),
        api.scopes(),
        api.documents(),
      ])
      setCandidates(found)
      setScopes(groups)
      setDocs(registry.filter((d) => d.status !== 'superseded'))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function key(c: Promotable) {
    return c.rule_ids.join('|')
  }

  function begin(c: Promotable) {
    setOpen(key(c))
    setDone(null)
    setError(null)
    setForm({
      scopeId: '',
      documentId: docs.length === 1 ? (docs[0]?.id ?? '') : '',
      clause: '',
    })
  }

  async function promote(c: Promotable) {
    const scope = scopes.find((s) => s.id === form.scopeId)
    if (!scope || !form.documentId || !form.clause.trim()) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.propose({
        // Named for the group and what it constrains. A key carrying a fund's
        // code would say this is that fund's limit, which is the thing being
        // undone.
        rule_key: `${scope.code.toLowerCase()}.${c.measure ?? c.template}`,
        // **`house`, never the candidate's own layer.** A scope rule *is* firm
        // policy — `mandate_is_portfolio_scoped` refuses a contractual limit on
        // a group, because a contract binds a counterparty, not a region. Eight
        // funds whose contracts each say 20% are eight contracts; promoting
        // them says instead that the firm imposes 20% on everything in the
        // group, which is a different and weaker claim about each fund and the
        // only one a group can carry.
        layer: 'house',
        template: c.template,
        subject: c.subject,
        evaluation_scope: c.evaluation_scope,
        machine_checkable: true,
        measure: c.measure,
        basis: c.basis,
        operator: c.operator,
        threshold: c.threshold,
        scope_id: form.scopeId,
        promoted_from_funds: fundsFor(scope, c.portfolio_codes, c.funds_by_desk ?? {}),
        source_document_id: form.documentId,
        source_clause: form.clause.trim(),
        effective_from: new Date().toISOString(),
        // §8.6: the only channel permitted to attach a rule to a scope.
        channel: 'portal',
      })
      setDone(
        `Proposed as ${result.rule_key} on ${scope.code}. An approver decides it, ` +
          `and is shown every fund the group reaches first. The ${c.rule_ids.length} ` +
          'fund-level copies stay in force until they are superseded.',
      )
      setOpen(null)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="promotions">
      <header className="page-head">
        <h2>Limits repeated across funds</h2>
        <p className="note">
          The same limit written out on several funds may be one firm-wide policy
          or several independent negotiations — nothing in the data tells you
          which, so nothing here decides. Where it is one policy, promoting it to
          a group records it once, and every fund in that group inherits it.
        </p>
      </header>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {done && <p className="done">{done}</p>}

      {firmWide && new Set(candidates.flatMap((c) => c.desk_codes ?? [])).size > 1 && (
        <label>
          Desk
          <select aria-label="Show copies on" value={deskShown} onChange={(e) => setDeskShown(e.target.value)}>
            <option value="">Every desk</option>
            {[...new Set(candidates.flatMap((c) => c.desk_codes ?? []))].sort().map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
      )}

      {candidates.length === 0 ? (
        <p className="empty" data-testid="no-candidates">
          No limit stands identically on two or more funds. Nothing to promote.
        </p>
      ) : (
        <table className="grid limits has-menus">
          <thead>
            <tr>
              <th>Constrains</th>
              <th>Says</th>
              <th>Set by</th>
              <th>On</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {candidates
              .filter((c) => !deskShown || (c.desk_codes ?? []).includes(deskShown))
              .map((c) => {
              const said = says(c)
              const showing = open === key(c)
              return (
                <tr key={key(c)} data-testid="promotable">
                  <td>
                    {c.measure}
                    <span className="subject">{subjectText(c.subject)}</span>
                  </td>
                  <td>
                    <span className={said.numeric ? 'limit' : 'no-number'}>
                      {said.text}
                    </span>
                  </td>
                  <td>
                    <span className="pill pill-layer">{layerLabel(c.layer)}</span>
                  </td>
                  <td>
                    <strong>{c.portfolio_codes.length} funds</strong>
                    <span className="clause">{c.portfolio_codes.join(', ')}</span>
                    {firmWide && Object.keys(c.funds_by_desk ?? {}).length > 0 && (
                      <span className="clause" data-testid="promotable-desks">
                        {Object.entries(c.funds_by_desk ?? {})
                          .map(([desk, codes]) => `${desk}: ${codes.length}`)
                          .join(' · ')}
                      </span>
                    )}
                  </td>
                  <td>
                    {!canAttach ? (
                      <span className="clause">
                        An author or approver proposes this; an approver then
                        decides it, having been shown every fund the group
                        reaches.
                      </span>
                    ) : showing ? (
                      <form
                        className="cite-form"
                        data-testid="promote-form"
                        onSubmit={(e) => {
                          e.preventDefault()
                          void promote(c)
                        }}
                      >
                        <label>
                          Group
                          <select
                            value={form.scopeId}
                            required
                            onChange={(e) => setForm({ ...form, scopeId: e.target.value })}
                          >
                            <option value="">Which group?</option>
                            {groupsFor(scopes, c.desk_codes ?? [], firmWide).map((s) => (
                              <option key={s.id} value={s.id}>
                                {groupLabel(s, c.portfolio_codes, c.funds_by_desk ?? {})}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Document
                          <select
                            value={form.documentId}
                            required
                            title="The policy document that authorises this as one rule for the whole group. A scope rule citing nothing is refused."
                            onChange={(e) =>
                              setForm({ ...form, documentId: e.target.value })
                            }
                          >
                            <option value="">Which document authorises it?</option>
                            {docs.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.title}
                                {d.version_label ? ` · ${d.version_label}` : ''}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          Clause
                          <input
                            value={form.clause}
                            required
                            placeholder="§3"
                            onChange={(e) => setForm({ ...form, clause: e.target.value })}
                          />
                        </label>
                        <button type="submit" disabled={busy}>
                          {busy ? 'Proposing…' : 'Propose it on the group'}
                        </button>
                        <button type="button" className="ghost" onClick={() => setOpen(null)}>
                          Cancel
                        </button>
                        {/* Said before the click, not after. The store cannot
                            close twelve rules with one proposal — `supersedes_id`
                            names a single rule — so this adds one and leaves the
                            copies standing, and Lookup will show both. */}
                        <p className="note" data-testid="copies-remain">
                          This proposes <strong>one</strong> limit on the group,{' '}
                          {c.layer === 'mandate' ? (
                            <>
                              as <strong>house policy</strong> — a group cannot
                              carry a contractual limit, because a contract binds
                              a counterparty rather than a region. You are saying
                              the firm imposes this on every fund in the group,
                              not that their mandates agree.{' '}
                            </>
                          ) : (
                            <>as house policy. </>
                          )}
                          The {c.rule_ids.length} fund-level copies —{' '}
                          {c.rule_keys.join(', ')} — stay in force until each is
                          superseded, and until then both will show against those
                          funds.
                        </p>
                      </form>
                    ) : (
                      <RowMenu
                        label={`Actions for ${c.measure ?? 'the limit'} on ${c.portfolio_codes.length} funds`}
                        items={[{ label: 'Promote to a group', onSelect: () => begin(c) }]}
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
